import type { HeaderSettings } from "@/utils/header-state";

interface HeaderHeights {
  type?: unknown;
  heightMobile?: unknown;
  heightDesktop?: unknown;
  heightMobileVisible?: unknown;
  heightDesktopVisible?: unknown;
  floating?: object;
}

/**
 * The header configuration with the look's heights in its own height keys, so the header, its logo and the content
 * offset all follow them. Standard and Gennemsigtig have a height per state, and »Ingen ændring« keeps the top height
 * after the switch; Flydende takes Højde øverst as its bar's height. Klassisk is returned as it is, since header-look.css
 * sizes it from the look. Without look heights the configuration itself is returned.
 */
export function withLookHeights<T extends HeaderHeights>(
  header: T,
  settings: HeaderSettings | undefined,
): T {
  const { top, after } = settings?.height ?? {};
  const type = header.type ?? "DefaultHeader";
  if (type === "FloatingHeader")
    return top
      ? {
          ...header,
          floating: {
            ...header.floating,
            height: `${top.mobile} | ${top.desktop}`,
          },
        }
      : header;
  if (type !== "DefaultHeader" && type !== "TransparentHeader") return header;
  if (!top && !after) return header;
  const mobile = top?.mobile || header.heightMobile || "80px";
  const desktop =
    top?.desktop ||
    header.heightDesktop ||
    (type === "DefaultHeader" ? "100px" : mobile);
  const visible = after === "same" ? { mobile, desktop } : after;
  return {
    ...header,
    ...(top ? { heightMobile: top.mobile, heightDesktop: top.desktop } : {}),
    ...(visible
      ? {
          heightMobileVisible: visible.mobile,
          heightDesktopVisible: visible.desktop,
        }
      : {}),
  };
}

/** The corner tokens the CMS writes, in the px the CMS shows for them. */
const CORNERS: Record<string, string> = {
  none: "0px",
  sm: "2px",
  md: "6px",
  lg: "8px",
  xl: "12px",
  "2xl": "16px",
  "3xl": "24px",
  full: "999px",
};

/** `floating.corners` (Menubarens hjørner): a corner token or an exact px length; anything else keeps Helt rund. */
export function floatingBarRadius(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (Object.hasOwn(CORNERS, value)) return CORNERS[value];
  return /^\d+(\.\d+)?px$/.test(value) ? value : undefined;
}
